import cuid from "cuid";
import exifr from "exifr";

import {
  buildVersion,
  extractFileExtension,
  inferExtensionFromMimeType,
  renameFileWithExtension,
  resolveUploadMimeParts,
  truncate,
} from "@/utils";

export type FileMetaType = {
  id: string;
  type: string;
  codec: string;
  itemId: string;
  contentId: string;
  filename: string;
  name: string;
  filetype: string;
  size: number;
  extension: string;
  surface: string;
  surfaceContext: string | undefined;
  uniqueUploadId: string;
  surfaceBuild: string;
  isSilent: boolean;
  inputType: string;
  requiresTitleAndDescription: boolean;
  requiresExtension: boolean;
  longitude?: string;
  latitude?: string;
  model?: string;
  gpsAltitude?: string;
  createDate?: string;
  orientation?: string;
  title?: string;
  description?: string;
};

export type FileWithMetaType = {
  file: File;
  meta: FileMetaType;
};

/**
 * Convert all meta values to string for UPPY META
 * @param o
 */
export const toString = (o) => {
  Object.keys(o).forEach((k) => {
    if (typeof o[k] === "object" && o[k] !== null) {
      return toString(o[k]);
    }

    o[k] = "" + o[k];
  });

  return o;
};

/**
 * Normalize EXIF data
 * @param exifData
 */
const normalizeExifData = (exifData) => ({
  longitude: exifData?.longitude?.toString() ?? "",
  latitude: exifData?.latitude?.toString() ?? "",
  model: exifData?.Model?.toString() ?? "",
  gpsAltitude: exifData?.GPSAltitude?.toString() ?? "",
  createDate: exifData?.CreateDate?.toString() ?? "",
  orientation: exifData?.Orientation?.toString() ?? "",
});

/**
 * Read EXIF data from file if it's available
 * If it's not it should fail silently
 * @param file
 */
const readExifData = async (file: File) => {
  try {
    return await exifr.parse(file);
  } catch (e) {
    return;
  }
};

export const preventDefaults = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
};

export const removeAllItemsFromStorage = (pattern: string) => {
  const hasLocalStorage = typeof localStorage !== "undefined";
  const hasSessionStorage = typeof sessionStorage !== "undefined";
  if (!hasLocalStorage && !hasSessionStorage) {
    return;
  }

  const storageTypes = [localStorage, sessionStorage];
  for (const type of storageTypes) {
    for (let i = type.length - 1; i >= 0; i--) {
      const key = type.key(i);
      if (key && key.startsWith(pattern)) {
        type.removeItem(key);
      }
    }
  }
};

export const parseFile = async (file: File, extraMeta?: Record<string, any>): Promise<FileWithMetaType> => {
  if (!file) {
    return;
  }

  let workingFile = file;
  let extension = extractFileExtension(workingFile.name);
  if (!extension) {
    const inferred = inferExtensionFromMimeType(workingFile.type);
    if (inferred) {
      workingFile = renameFileWithExtension(workingFile, inferred);
      extension = inferred;
    }
  }

  const exifData = await readExifData(workingFile);
  const normalizedExif = exifData ? normalizeExifData(exifData) : {};
  const { type, codec, filetype } = resolveUploadMimeParts(workingFile);
  const itemId = cuid();
  const contentId = cuid();
  const name = truncate(workingFile.name, 30, "...");

  return {
    file: workingFile,
    meta: {
      ...normalizedExif,
      id: cuid(),
      type,
      codec,
      itemId,
      contentId,
      uniqueUploadId: contentId,
      filename: workingFile.name,
      name,
      filetype,
      size: workingFile.size,
      extension,
      surface: "web",
      surfaceContext: window?.navigator?.userAgent,
      surfaceBuild: buildVersion(),
      isSilent: true,
      inputType: "File",
      requiresTitleAndDescription: type === "audio",
      requiresExtension: !extension,
      ...extraMeta,
    },
  };
};
